import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import { ChargeConstants } from 'features/Charge/chargeHelper';
import SystemHelper from 'features/System/systemHelper';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTFormTextArea from 'general/components/OtherKeenComponents/Forms/KTFormTextArea';
import KTCheckbox, {
  KTCheckboxType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTCheckbox';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import permissionHelper from 'general/helpers/permissionHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import StringUtils from 'general/utils/StringUtils';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

DialogAddPermissionGroupView.propTypes = {
  role: PropTypes.string,
  subRole: PropTypes.string,
  permissionList: PropTypes.array,
  item: PropTypes.object,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
  removeFooter: PropTypes.bool,
};

function DialogAddPermissionGroupView({ onCancel, onDone, item, permissionList, role, subRole, removeFooter = false }) {
  // MARK: ---- PROPERTIES ----
  const [selectedSubRole, setSelectedSubRole] = useState();
  const subRoleList = useMemo(() => {
    return _.split(subRole, ',');
  }, [subRole]);
  useEffect(() => {
    if (!_.isEmpty(subRoleList)) {
      setSelectedSubRole(subRoleList[0]);
    }
  }, [subRoleList]);
  const editMode = !!item;
  const [loading, setLoading] = useState(false);
  const [readyToValidate, setReadyToValidate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  useEffect(() => {
    if (item) {
      setName(item?.name);
      setDescription(item?.description);
      setSelectedPermissions(item?.permissions?.map((permission) => permission.permissionId));
      setSelectedSubRole(item?.subRole);
    }
  }, [item]);
  const [agencyPermissionList, setAgencyPermissionList] = useState(null);

  useEffect(() => {
    if (role === ChargeConstants.UserRole.AGENCY) {
      const newAgencyPermissionList = {};

      subRoleList?.forEach(subRole => {
        newAgencyPermissionList[subRole] = permissionList?.filter(permission =>
          permission?.subRole?.length && permission?.subRole?.includes(subRole)
        ) || [];
      });

      setAgencyPermissionList(newAgencyPermissionList);
    }
  }, [role, subRoleList, permissionList]);

  const [searchText, setSearchText] = useState('');
  const [showPermissionList, setShowPermissionList] = useState(null);

  useEffect(() => {
    let showPermissions = permissionList;

    const ADMIN_HIDDEN_PREFIXES = [
      'ENERGY_BILL',
      'GROUP_',
      'INVESTMENT_PACK',
      'INVESTOR_',
      'LANDLORD_',
      'PREMISE_',
    ];
    const AGENCY_HIDDEN_PREFIXES = [
      'ENERGY_BILL',
      'INVESTOR_',
      'LANDLORD_',
    ];

    const codeOrderList = permissionHelper.getPermissionCodeOrder(role, selectedSubRole);

    if (role === ChargeConstants.UserRole.ADMIN) {
      const adminPermissionList = permissionList?.filter(permission =>
        permission?.role?.length && permission?.role?.includes('ADMIN')
      ) || [];
      showPermissions = permissionHelper.sortPermissionsByCodeOrder(
        adminPermissionList,
        codeOrderList
      );
      showPermissions = showPermissions.filter(item =>
        !ADMIN_HIDDEN_PREFIXES.some(prefix => item.code?.startsWith(prefix))
      );
    } else {
      if (!agencyPermissionList || !selectedSubRole) return;
      const agencyList = agencyPermissionList[selectedSubRole] ?? [];
      showPermissions = permissionHelper.sortPermissionsByCodeOrder(agencyList, codeOrderList);
      showPermissions = showPermissions.filter(item =>
        !AGENCY_HIDDEN_PREFIXES.some(prefix => item.code?.startsWith(prefix))
      );
    }
    showPermissions = showPermissions.map((item) => {
      const subItemNum = showPermissions.filter(
        (subItem) => subItem.parentPermissionId === item.permissionId
      ).length;
      return { ...item, subItemNum };
    });

    let filterShowPermissions = showPermissions;

    if (searchText) {
      filterShowPermissions = _.filter(showPermissions, (item) => {
        const itemName = StringUtils.removeVietnameseTones(item?.name ?? '').toLowerCase();
        return itemName?.includes(
          StringUtils.removeVietnameseTones(searchText ?? '').toLowerCase()
        );
      });
    }

    const finalFilterShowPermissions = [...filterShowPermissions];
    filterShowPermissions.forEach((item) => {
      if (item.parentPermissionId) {
        const parentExists = finalFilterShowPermissions.some(
          (perm) => perm.permissionId === item.parentPermissionId
        );
        if (!parentExists) {
          const parentPermission = showPermissions.find(
            (perm) => perm.permissionId === item.parentPermissionId
          );
          if (parentPermission) {
            finalFilterShowPermissions.push(parentPermission);
          }
        }
      }
    });

    setShowPermissionList(finalFilterShowPermissions);
  }, [role, permissionList, agencyPermissionList, selectedSubRole, searchText]);

  // MARK: ---- FUNCTIONS ----
  const handleSubmit = async () => {
    setReadyToValidate(true);
    if (!Utils.isValid(name)) {
      ToastHelper.showError('Bạn chưa nhập tên nhóm quyền');
      return;
    }
    let params = {
      name: name,
      description: description,
      permissions: selectedPermissions,
      role: role,
      subRole: selectedSubRole,
    };
    params = _.pickBy(params, (value) => {
      return value !== undefined && value !== null && value !== '';
    });
    setLoading(true);
    if (!editMode) {
      // Thêm mới nhóm quyền
      const res = await SystemHelper.API.Permission.requestAddPermissionGroup(params);
      if (_.isBoolean(res) && res) {
        ToastHelper.showSuccess('Thêm mới nhóm quyền thành công');
        onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    } else {
      // Cập nhật nhóm quyền
      const res = await SystemHelper.API.Permission.requestUpdatePermissionGroup(
        item.permissionGroupId,
        params
      );
      if (_.isBoolean(res) && res) {
        ToastHelper.showSuccess('Cập nhật nhóm quyền thành công');
        onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    }
    setLoading(false);
  };

  const handleSetPermissionList = (role, subRole) => {
    if (role === ChargeConstants.UserRole.ADMIN) {
      return permissionList;
    } else {
      return agencyPermissionList[setSelectedSubRole];
    }
  }

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleGroupToggle = (group) => {
    const groupPermissionIds = group.children.map((child) => child.permissionId);
    const isGroupSelected = groupPermissionIds.every((id) => selectedPermissions.includes(id));

    setSelectedPermissions((prev) =>
      isGroupSelected
        ? prev.filter((id) => !groupPermissionIds.includes(id))
        : [...prev, ...groupPermissionIds.filter((id) => !prev.includes(id))]
    );
  };

  const buildPermissionTree = (permissions) => {
    if (!permissions || !permissions?.length) return;

    const map = {};
    const roots = [];

    // Tạo map để dễ dàng truy cập các permissions
    permissions.forEach((permission) => {
      map[permission.permissionId] = { ...permission, children: [] };
    });

    // Gom nhóm các permissions theo parentPermissionId
    permissions.forEach((permission) => {
      if (permission.parentPermissionId) {
        map[permission.parentPermissionId]?.children.push(map[permission.permissionId]);
      } else {
        roots.push(map[permission.permissionId]);
      }
    });

    return roots;
  };

  const renderPermissionTree = (tree) => {
    if (!tree || !tree?.length) return;
    return tree.map((node) => (
      <div key={node.permissionId} className="mb-4">
        {/* Nhóm quyền */}
        {node.children.length > 0 && (
          <div
            className="px-4 pb-2"
            style={{
              borderBottom: `1px solid ${AppColors.gray[200]}`,
            }}
          >
            <label className="d-flex align-items-center">
              <KTCheckbox
                name={`checkbox_${node.permissionId}`}
                additionalClassName="checkbox-success"
                label={<div className="ms-2">{node.name}</div>}
                type={KTCheckboxType.default}
                onChange={() => handleGroupToggle(node)}
                checked={node.children.every((child) =>
                  selectedPermissions.includes(child.permissionId)
                ) && node?.children?.length === node?.subItemNum}
              />
            </label>
            <div className="row mt-2 pl-6">
              {node.children.map((child) => (
                <div key={child.permissionId} className="col-6">
                  <label className="d-flex align-items-center">
                    <KTCheckbox
                      name={`checkbox_${child.permissionId}`}
                      additionalClassName="checkbox-success"
                      label={<div className="ms-2">{child.name}</div>}
                      type={KTCheckboxType.default}
                      checked={selectedPermissions.includes(child.permissionId)}
                      onChange={() => handlePermissionToggle(child.permissionId)}
                    />
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Quyền không thuộc nhóm */}
        {node.children.length === 0 && (
          <div className="px-4 pb-2" style={{ borderBottom: `1px solid ${AppColors.gray[200]}` }}>
            <label className="d-flex align-items-center">
              <KTCheckbox
                additionalClassName="checkbox-success"
                name={`checkbox_${node.permissionId}`}
                label={<div className="ms-2">{node.name}</div>}
                type={KTCheckboxType.default}
                checked={selectedPermissions.includes(node.permissionId)}
                onChange={() => handlePermissionToggle(node.permissionId)}
              />
            </label>
          </div>
        )}
      </div>
    ));
  };

  return (
    <div>
      <div style={{ height: removeFooter ? '90vh' : '80vh', overflowY: 'auto' }}>
        <div className="row m-0 p-0 px-2 mt-4">
          {/* ten nhom quyen */}
          <div className="col-12 px-2 mb-4">
            <div>
              {UIHelper.buildFormLabel('Tên nhóm quyền', true)}
              <KTFormInput
                type={KTFormInputType.text}
                placeholder="Nhập tên nhóm quyền"
                name="name"
                value={name}
                onChange={(value) => {
                  setName(value);
                }}
                enableCheckValid={readyToValidate}
                isValid={Utils.isValid(name)}
                isTouched={readyToValidate}
                feedbackText={!Utils.isValid(name) ? 'Tên nhóm quyền không được để trống' : ''}
              />
            </div>
          </div>
          {/* mo ta */}
          <div className="col-12 px-2 mb-4">
            <div>
              {UIHelper.buildFormLabel('Mô tả')}
              <KTFormTextArea
                rows={3}
                placeholder="Nhập mô tả"
                name="description"
                value={description}
                onChange={(value) => {
                  setDescription(value);
                }}
              />
            </div>
          </div>
          {/* subRole */}
          {subRoleList.length > 1 && (
            <div className="col-12 mb-2 px-2">
              {UIHelper.buildFormLabel('Vai trò đối tác')}
              <div className="radio-inline w-100 mt-1">
                <div className="d-flex align-items-center gap-5">
                  {subRoleList.map((subRole, index) => {
                    const isDisabled = subRole === AgencyConstants.AgencyRole.GROUP;
                    return (
                      <div key={subRole} style={isDisabled ? { opacity: 0.6 } : {}}>
                        <KTRadio
                          name={subRole}
                          label={AgencyHelper.Utils.getAgencyRoleName(subRole)}
                          type={KTRadioType.outline}
                          additionalClassName="radio-success"
                          onChange={() => {
                            if (!isDisabled) setSelectedSubRole(subRole);
                          }}
                          checked={selectedSubRole === subRole}
                          disabled={isDisabled}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="px-2 mb-4">
            <KTFormSearch
              name="searchbar"
              value={searchText}
              placeholder="Tìm theo tính năng"
              onSubmit={(text) => {
                setSearchText(text);
              }}
            />
          </div>

          {/* danh sach quyen */}
          <div className="col-12 px-2 mb-4">
            {UIHelper.buildSectionCard(
              <div className="py-4">
                {renderPermissionTree(buildPermissionTree(showPermissionList))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* actions */}
      {!removeFooter && (
        <div
          className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
          style={{
            borderTop: `1px solid ${AppColors.gray[200]}`,
          }}
        >
          {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
          {UIHelper.buildGreenButton(
            !editMode ? 'Thêm' : 'Cập nhật',
            handleSubmit,
            null,
            false,
            loading
          )}
        </div>
      )}
    </div>
  );
}

export default DialogAddPermissionGroupView;
